// Planning d'un chantier (incrément 3) : affecter des personnes à un poste (et, si on veut, à des étapes précises) sur des jours ;
// conflits et postes en sous-effectif SIGNALÉS (jamais bloquants) ; couverture « heures affectées / heures prévues restantes ».
// Une affectation retirée reste lisible, barrée. Aucun montant. Le serveur décide seul des droits (droits.affecter).
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarPlus, ChevronLeft, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { nombre, pluriel } from '../../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EtatErreur, EtatVide, useNotifier } from '../../../components/ui.tsx';
import { ajouterJours, CRENEAUX, estWeekEnd, jourCourt, jourMois, lundiDe } from './jours.ts';
import type { AffectationChantier, Creneau, PlanningChantier } from './types.ts';

export const heuresTxt = (h: string | null | undefined) => (h === null || h === undefined ? null : `${nombre(h, 2)} h`);

/** Navigation d'une semaine à l'autre (boutons de 44 px, libellés explicites). */
export function NavigationSemaine({ du, aujourdhui, changer }: { du: string; aujourdhui: string; changer: (du: string) => void }) {
  const cetteSemaine = lundiDe(aujourdhui);
  return (
    <div className="pl-semaine" role="group" aria-label="Semaine affichée">
      <Bouton compact variante="fantome" icone={<ChevronLeft aria-hidden />} onClick={() => changer(ajouterJours(du, -7))} aria-label="Semaine précédente">
        <span className="masque-etroit">Précédente</span></Bouton>
      <span className="pl-semaine-libelle"><span className="masque-etroit">Semaine du </span><span className="visible-etroit">Sem. du </span>{jourMois(du)}</span>
      {du !== cetteSemaine && <Bouton compact variante="fantome" onClick={() => changer(cetteSemaine)}>Cette semaine</Bouton>}
      <Bouton compact variante="fantome" onClick={() => changer(ajouterJours(du, 7))} aria-label="Semaine suivante">
        <span className="masque-etroit">Suivante</span><ChevronRight aria-hidden /></Bouton>
    </div>
  );
}

// ─── Affecter ───────────────────────────────────────────────────────────────────────────────────
interface ChantierChoisissable { id: string; reference: string | null; title: string }

/**
 * Affecter une ou plusieurs personnes, sur un ou plusieurs jours de la semaine, à un poste (ou à tout le chantier), avec, si on
 * veut, des étapes précises, un créneau, des heures prévues et une note. Sans chantier donné : choix du chantier (planning du bureau).
 */
export function DialogueAffecter({ chantierId, chantiers, semaine, joursInitiaux, personneInitiale, fermer }: {
  chantierId?: string; chantiers?: ChantierChoisissable[]; semaine: string[]; joursInitiaux: string[]; personneInitiale?: string; fermer: () => void;
}) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [chantier, setChantier] = useState(chantierId ?? (chantiers?.length === 1 ? chantiers[0].id : ''));
  const [jours, setJours] = useState<string[]>(joursInitiaux);
  const [gens, setGens] = useState<string[]>(personneInitiale ? [personneInitiale] : []);
  const [poste, setPoste] = useState('');
  const [etapes, setEtapes] = useState<string[]>([]);
  const [creneau, setCreneau] = useState<Creneau>('journee');
  const [heures, setHeures] = useState('');
  const [heuresTouchees, setHeuresTouchees] = useState(false);
  const [note, setNote] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const plan = useQuery({ queryKey: ['planning-chantier', chantier, semaine[0]], enabled: !!chantier,
    queryFn: () => api.get<PlanningChantier>(`/api/chantiers/${chantier}/affectations?du=${semaine[0]}&jours=7`) });
  const personnes = useQuery({ queryKey: ['chantier-personnes', chantier], enabled: !!chantier,
    queryFn: () => api.get<{ id: string; nom: string }[]>(`/api/chantiers/${chantier}/personnes`) });
  const postes = (plan.data?.postes ?? []).filter((p) => !p.retire);
  const posteChoisi = postes.find((p) => p.id === poste);
  // Heures proposées d'après les « heures sur site par jour » du chantier (ou de l'entreprise) : visibles et modifiables, jamais
  // posées en silence ; sans hypothèse saisie, le champ reste vide (« non précisées »).
  useEffect(() => {
    if (heuresTouchees || !plan.data?.heures_par_jour) return;
    const h = Number(plan.data.heures_par_jour);
    setHeures(String(creneau === 'journee' ? h : Math.round((h / 2) * 100) / 100).replace('.', ','));
  }, [plan.data?.heures_par_jour, creneau, heuresTouchees]);
  const basculer = (l: string[], x: string) => (l.includes(x) ? l.filter((y) => y !== x) : [...l, x]);

  const valider = async () => {
    setErreur(null);
    if (!chantier) { setErreur('Choisissez le chantier.'); return; }
    if (!jours.length) { setErreur('Choisissez au moins un jour.'); return; }
    if (!gens.length) { setErreur('Choisissez au moins une personne.'); return; }
    setEnvoi(true);
    try {
      const r = await api.post<{ creees: string[]; deja: string[]; avertissements: string[] }>(`/api/chantiers/${chantier}/affectations`, {
        jours, personnes: gens, posteId: poste || null, etapeIds: etapes, creneau, heures: heures.trim() || null, note: note.trim() || null });
      await Promise.all([qc.invalidateQueries({ queryKey: ['planning-chantier'] }), qc.invalidateQueries({ queryKey: ['planning'] }),
        qc.invalidateQueries({ queryKey: ['ma-journee'] })]);
      notifier(r.creees.length ? `${pluriel(r.creees.length, 'affectation enregistrée', 'affectations enregistrées')}${r.deja.length ? ` (${r.deja.length} existait déjà)` : ''}.`
        : 'Rien de nouveau : ces affectations existaient déjà.');
      for (const a of r.avertissements.slice(0, 3)) notifier(`À vérifier : ${a}`, true);
      fermer();
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };

  return (
    <Dialogue titre="Affecter" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" chargement={envoi} icone={<CalendarPlus aria-hidden />} onClick={valider}>Affecter</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {!chantierId && (
          <ChampChoix libelle="Chantier" value={chantier} vide="Choisir un chantier en cours"
            onChange={(e) => { setChantier(e.target.value); setGens([]); setPoste(''); setEtapes([]); }}
            options={(chantiers ?? []).map((c) => ({ valeur: c.id, libelle: `${c.reference ?? ''} ${c.title}`.trim() }))} />
        )}
        <fieldset className="pl-cases">
          <legend className="champ-libelle">Jours</legend>
          <div className="pl-jours-choix">
            {semaine.map((j) => (
              <label key={j} className={`pl-choix ${jours.includes(j) ? 'est-choisi' : ''} ${estWeekEnd(j) ? 'pl-we' : ''}`}>
                <input type="checkbox" checked={jours.includes(j)} onChange={() => setJours((l) => basculer(l, j).sort())} />{jourCourt(j)}
              </label>
            ))}
          </div>
        </fieldset>
        {chantier && (plan.isLoading || personnes.isLoading) && <Chargement lignes={2} />}
        {(plan.isError || personnes.isError) && <EtatErreur erreur={plan.error ?? personnes.error} />}
        {plan.data && plan.data.droits.motif_lecture_seule && <Alerte ton="ambre">{plan.data.droits.motif_lecture_seule}</Alerte>}
        {personnes.data && (
          <fieldset className="pl-cases">
            <legend className="champ-libelle">Personnes (compte actif ayant accès au site)</legend>
            {personnes.data.length === 0 ? <p className="petit discret">Personne n’a accès à ce site : l’administrateur doit d’abord ouvrir l’accès.</p> : (
              <div className="pl-personnes-choix">
                {personnes.data.map((p) => (
                  <label key={p.id} className={`pl-choix ${gens.includes(p.id) ? 'est-choisi' : ''}`}>
                    <input type="checkbox" checked={gens.includes(p.id)} onChange={() => setGens((l) => basculer(l, p.id))} />{p.nom}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}
        {plan.data && (
          <>
            <ChampChoix libelle="Poste" value={poste} onChange={(e) => { setPoste(e.target.value); setEtapes([]); }} vide="Tout le chantier (sans poste précis)"
              options={postes.map((p) => ({ valeur: p.id, libelle: `${p.label}${p.lieu ? ` — ${p.lieu}` : ''}${p.crew_min && p.crew_min > 1 ? ` — ${p.crew_min} personnes` : ''}` }))} />
            {posteChoisi && posteChoisi.etapes.length > 0 && (
              <fieldset className="pl-cases">
                <legend className="champ-libelle">Étapes précises (facultatif : sans choix, tout le poste)</legend>
                <div className="pl-etapes-choix">
                  {posteChoisi.etapes.map((e) => (
                    <label key={e.id} className={`pl-choix pl-choix-large ${etapes.includes(e.id) ? 'est-choisi' : ''}`}>
                      <input type="checkbox" checked={etapes.includes(e.id)} onChange={() => setEtapes((l) => basculer(l, e.id))} />
                      <span>{e.label}{e.hours_planned !== null ? <span className="petit discret"> — {heuresTxt(e.hours_planned)}</span> : null}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <div className="pl-ligne-champs">
              <ChampChoix libelle="Créneau" value={creneau} onChange={(e) => setCreneau(e.target.value as Creneau)}
                options={(Object.keys(CRENEAUX) as Creneau[]).map((c) => ({ valeur: c, libelle: CRENEAUX[c] }))} />
              <Champ libelle="Heures prévues par personne et par jour" inputMode="decimal" value={heures} placeholder="Non précisées"
                onChange={(e) => { setHeures(e.target.value); setHeuresTouchees(true); }}
                aide={plan.data.heures_par_jour && !heuresTouchees ? `Proposé d’après ${nombre(plan.data.heures_par_jour, 2)} h sur site par jour : à corriger si besoin.` : 'Vide = non précisées.'} />
            </div>
            <ChampTexte libelle="Note (facultatif)" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. : prendre la nacelle" />
          </>
        )}
      </div>
    </Dialogue>
  );
}

/** Modifier le créneau, les heures, la note ou les étapes précises d'une affectation. */
function DialogueModifier({ chantierId, a, plan, fermer }: { chantierId: string; a: AffectationChantier; plan: PlanningChantier; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [creneau, setCreneau] = useState<Creneau>(a.creneau);
  const [heures, setHeures] = useState(a.heures ? nombre(a.heures, 2) : '');
  const [note, setNote] = useState(a.note ?? '');
  const [etapes, setEtapes] = useState<string[]>(a.etape_ids);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const poste = plan.postes.find((p) => p.id === a.poste?.id);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.patch<{ avertissements: string[] }>(`/api/chantiers/${chantierId}/affectations/${a.id}`, {
        creneau, heures: heures.trim() || null, note: note.trim() || null, ...(poste ? { etapeIds: etapes } : {}) });
      await Promise.all([qc.invalidateQueries({ queryKey: ['planning-chantier'] }), qc.invalidateQueries({ queryKey: ['planning'] }),
        qc.invalidateQueries({ queryKey: ['ma-journee'] })]);
      notifier('Affectation modifiée.');
      for (const x of r.avertissements.slice(0, 3)) notifier(`À vérifier : ${x}`, true);
      fermer();
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Dialogue titre={`${a.personne.nom} — ${jourCourt(a.jour)}`} ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={valider}>Enregistrer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <p className="petit secondaire" style={{ margin: 0 }}>{a.poste ? `Poste : ${a.poste.label}` : 'Tout le chantier'}. Pour changer de jour, de personne ou de poste : retirez cette affectation, puis affectez de nouveau.</p>
        {poste && poste.etapes.length > 0 && (
          <fieldset className="pl-cases">
            <legend className="champ-libelle">Étapes précises (sans choix : tout le poste)</legend>
            <div className="pl-etapes-choix">
              {poste.etapes.map((e) => (
                <label key={e.id} className={`pl-choix pl-choix-large ${etapes.includes(e.id) ? 'est-choisi' : ''}`}>
                  <input type="checkbox" checked={etapes.includes(e.id)} onChange={() => setEtapes((l) => (l.includes(e.id) ? l.filter((y) => y !== e.id) : [...l, e.id]))} />
                  <span>{e.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="pl-ligne-champs">
          <ChampChoix libelle="Créneau" value={creneau} onChange={(e) => setCreneau(e.target.value as Creneau)}
            options={(Object.keys(CRENEAUX) as Creneau[]).map((c) => ({ valeur: c, libelle: CRENEAUX[c] }))} />
          <Champ libelle="Heures prévues" inputMode="decimal" value={heures} placeholder="Non précisées" onChange={(e) => setHeures(e.target.value)} />
        </div>
        <ChampTexte libelle="Note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialogue>
  );
}

/** Retirer : l'affectation reste visible, barrée, avec qui l'a retirée (motif facultatif). */
function DialogueRetirer({ chantierId, a, fermer }: { chantierId: string; a: AffectationChantier; fermer: () => void }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valider = async () => {
    setErreur(null); setEnvoi(true);
    try {
      await api.post(`/api/chantiers/${chantierId}/affectations/${a.id}/retirer`, { motif: motif.trim() || null });
      await Promise.all([qc.invalidateQueries({ queryKey: ['planning-chantier'] }), qc.invalidateQueries({ queryKey: ['planning'] }),
        qc.invalidateQueries({ queryKey: ['ma-journee'] })]);
      notifier('Affectation retirée (elle reste visible, barrée).');
      fermer();
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Dialogue titre="Retirer l’affectation" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="danger" chargement={envoi} onClick={valider}>Retirer</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <p style={{ margin: 0 }}>{a.personne.nom}, {jourCourt(a.jour)}{a.poste ? `, ${a.poste.label}` : ''}. Elle ne disparaîtra pas : elle restera visible, barrée, avec votre nom.</p>
        <ChampTexte libelle="Motif (facultatif)" rows={2} maxLength={500} value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. : absent ce jour" />
      </div>
    </Dialogue>
  );
}

// ─── Panneau « Planning » de la page d'un chantier ─────────────────────────────────────────────────
export function PlanningDuChantier({ chantierId }: { chantierId: string }) {
  const [du, setDu] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['planning-chantier', chantierId, du ?? 'courante'],
    queryFn: () => api.get<PlanningChantier>(`/api/chantiers/${chantierId}/affectations${du ? `?du=${du}` : ''}`) });
  const [affecter, setAffecter] = useState<{ jours: string[] } | null>(null);
  const [modif, setModif] = useState<AffectationChantier | null>(null);
  const [retrait, setRetrait] = useState<AffectationChantier | null>(null);
  const p = q.data;
  const parJour = useMemo(() => {
    const m = new Map<string, AffectationChantier[]>();
    for (const a of p?.affectations ?? []) m.set(a.jour, [...(m.get(a.jour) ?? []), a]);
    return m;
  }, [p]);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  if (!p) return null;
  const etapeLabel = (id: string) => p.postes.flatMap((x) => x.etapes).find((e) => e.id === id)?.label;
  const joursDefaut = p.jours.includes(p.aujourdhui) ? [p.aujourdhui] : [p.jours[0]];
  const c = p.couverture;
  return (
    <div className="pile">
      <div className="pl-barre">
        <NavigationSemaine du={p.du} aujourdhui={p.aujourdhui} changer={setDu} />
      </div>
      {p.droits.motif_lecture_seule && <Alerte ton="info">{p.droits.motif_lecture_seule}</Alerte>}
      {!p.droits.voir_tout && <p className="petit secondaire" style={{ margin: 0 }}>Vous voyez vos propres affectations sur ce chantier. Votre journée complète : Interventions › Ma journée.</p>}
      {c && (
        <p className="pl-couverture">
          Heures affectées à venir : <strong className="chiffre">{heuresTxt(c.heures_affectees_a_venir)}</strong>
          {' — '}heures prévues restantes : <strong className="chiffre">{heuresTxt(c.heures_prevues_restantes)}</strong>
          {Number(c.manque) > 0 && <>{' — '}<strong>il manque {heuresTxt(c.manque)} d’affectation</strong></>}
          {Number(c.heures_affectees_a_venir) > Number(c.heures_prevues_restantes) && <span className="petit secondaire"> — affecté au-delà du prévu restant : {heuresTxt(String(Math.round((Number(c.heures_affectees_a_venir) - Number(c.heures_prevues_restantes)) * 100) / 100))}</span>}
          {(c.affectations_sans_heures > 0 || c.etapes_sans_temps > 0) && (
            <span className="petit discret"> ({[c.affectations_sans_heures ? pluriel(c.affectations_sans_heures, 'affectation sans heures précisées', 'affectations sans heures précisées') : '',
              c.etapes_sans_temps ? pluriel(c.etapes_sans_temps, 'étape sans temps prévu', 'étapes sans temps prévu') : ''].filter(Boolean).join(', ')} : non comptées)</span>
          )}
        </p>
      )}
      {p.alertes.length > 0 && (
        <Alerte ton="ambre" titre={pluriel(p.alertes.length, 'point à vérifier', 'points à vérifier')}>
          <ul className="pl-alertes">{p.alertes.map((x, i) => <li key={i}>{x.message}</li>)}</ul>
        </Alerte>
      )}
      <ol className="pl-jours">
        {p.jours.map((j) => {
          const l = parJour.get(j) ?? [];
          return (
            <li key={j} className={`pl-jour ${j === p.aujourdhui ? 'pl-aujourdhui' : ''} ${estWeekEnd(j) ? 'pl-we' : ''}`}>
              <div className="pl-jour-entete">
                <span className="pl-jour-nom">{jourCourt(j)}{j === p.aujourdhui && <Badge ton="accent" icone={false}>Aujourd’hui</Badge>}</span>
                {p.droits.affecter && <Bouton compact variante="fantome" icone={<CalendarPlus aria-hidden />} onClick={() => setAffecter({ jours: [j] })}
                  aria-label={`Affecter le ${jourCourt(j)}`} title={`Affecter le ${jourCourt(j)}`} />}
              </div>
              {l.length === 0 ? <p className="petit discret pl-vide">Personne</p> : (
                <ul className="pl-affectations">
                  {l.map((a) => {
                    const alerte = p.alertes.some((x) => x.jour === j && ((x.type === 'conflit' && x.personne_id === a.personne.id) || (x.type === 'effectif' && x.poste_id === a.poste?.id)));
                    return (
                      <li key={a.id} className={`pl-affectation ${a.retiree ? 'ch-retire' : ''}`}>
                        <div className="pl-affectation-texte">
                          <span className="pl-personne">{alerte && !a.retiree && <AlertTriangle className="pl-alerte-icone" aria-label="Point à vérifier" />}{a.personne.nom}</span>
                          <span className="petit secondaire">{a.poste ? a.poste.label : 'Tout le chantier'}{a.poste?.lieu ? ` — ${a.poste.lieu}` : ''}{a.creneau !== 'journee' ? ` — ${CRENEAUX[a.creneau]}` : ''}{a.heures ? ` — ${heuresTxt(a.heures)}` : ''}</span>
                          {a.etape_ids.length > 0 && <span className="tres-petit discret">Étapes : {a.etape_ids.map(etapeLabel).filter(Boolean).join(' ; ')}</span>}
                          {a.note && <span className="tres-petit discret">Note : {a.note}</span>}
                          {a.retiree && <span className="tres-petit discret">Retirée{a.retiree.par ? ` par ${a.retiree.par}` : ''}{a.retiree.motif ? ` : ${a.retiree.motif}` : ''}</span>}
                        </div>
                        {p.droits.affecter && !a.retiree && (
                          <span className="pl-affectation-actions">
                            <Bouton compact variante="fantome" icone={<Pencil aria-hidden />} onClick={() => setModif(a)} aria-label={`Modifier l’affectation de ${a.personne.nom}`} />
                            <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} onClick={() => setRetrait(a)} aria-label={`Retirer l’affectation de ${a.personne.nom}`} />
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
      {p.affectations.length === 0 && !p.droits.voir_tout && <EtatVide titre="Aucune affectation pour vous cette semaine sur ce chantier" />}
      {/* Action principale toujours visible (barre collante commune, au-dessus des onglets du bas sur téléphone et tablette). */}
      {p.droits.affecter && (
        <div className="barre-actions barre-collante">
          <span className="petit chiffre barre-collante-resume">{pluriel(p.affectations.filter((a) => !a.retiree).length, 'affectation')} cette semaine{p.alertes.length ? ` · ${pluriel(p.alertes.length, 'point à vérifier', 'points à vérifier')}` : ''}</span>
          <Bouton variante="principal" icone={<CalendarPlus aria-hidden />} onClick={() => setAffecter({ jours: joursDefaut })}>Affecter</Bouton>
        </div>
      )}
      {affecter && <DialogueAffecter chantierId={chantierId} semaine={p.jours} joursInitiaux={affecter.jours} fermer={() => setAffecter(null)} />}
      {modif && <DialogueModifier chantierId={chantierId} a={modif} plan={p} fermer={() => setModif(null)} />}
      {retrait && <DialogueRetirer chantierId={chantierId} a={retrait} fermer={() => setRetrait(null)} />}
    </div>
  );
}

export function PanneauPlanning({ chantierId }: { chantierId: string }) {
  return <section className="pl-chantier" aria-label="Planning du chantier"><PlanningDuChantier chantierId={chantierId} /></section>;
}
